import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { SidebarService } from '../../services/sidebar/sidebar.service';
import { ScreenService } from '../../services/screen/screen.service';
import { PermissionsService } from '../../services/permissions/permissions.service';
import { map, combineLatest } from 'rxjs';

import { TEXT_CONSTANTS } from '../../constants/text-constants';
import { AuthService } from '../../services/auth/auth.service';
import { ActionService } from '../../services/action/action.service';

/**
 * Component for the application sidebar.
 * Handles navigation, responsiveness (mobile/desktop), and collapsing state.
 */
@Component({
  selector: 'sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css'
})
export class SidebarComponent {
  isCollapsed$ = this.sidebarService.isCollapsed$;
  private screenService = inject(ScreenService);
  private authService = inject(AuthService);
  private permissionsService = inject(PermissionsService);
  private router = inject(Router);
  private actionService = inject(ActionService);

  TEXT = TEXT_CONSTANTS;

  private allMenuItems = [
    {
      title: TEXT_CONSTANTS.SIDEBAR.DASHBOARD,
      icon: 'dashboard',
      route: '/dashboard',
      active: true
    },
    {
      title: TEXT_CONSTANTS.SIDEBAR.TRANSACTIONS,
      icon: 'payments',
      route: '/transactions',
      active: false,
    },
    {
      title: TEXT_CONSTANTS.SIDEBAR.FIXED_EXPENSES,
      icon: 'receipt_long',
      route: '/fixed-expenses',
      active: false
    },
    {
      title: TEXT_CONSTANTS.SIDEBAR.CARDS,
      icon: 'credit_card',
      route: '/cards',
      active: false,
    },
    {
      title: TEXT_CONSTANTS.SIDEBAR.GROUPS,
      icon: 'group',
      route: '/groups',
      active: false,
    },
    {
      title: TEXT_CONSTANTS.SIDEBAR.WALLETS,
      icon: 'account_balance_wallet',
      route: '/wallets',
      active: false,
    },
    {
      title: TEXT_CONSTANTS.SIDEBAR.ANALYTICS,
      icon: 'monitoring',
      route: '/analytics',
      active: false
    },
    {
      title: TEXT_CONSTANTS.SIDEBAR.SETTINGS,
      icon: 'settings',
      route: '/settings',
      active: false
    }
  ];

  private allBottomItems = [
    {
      title: TEXT_CONSTANTS.SIDEBAR.PRO,
      icon: 'crown',
      route: '/pro',
      active: false
    },
    {
      title: TEXT_CONSTANTS.SIDEBAR.HELP,
      icon: 'help',
      route: '/help',
      active: false
    }
  ];

  constructor(private sidebarService: SidebarService) { }

  menuItems$ = combineLatest([
    this.authService.userRole,
    this.permissionsService.permissionsReady$
  ]).pipe(
    map(() => this.allMenuItems.filter(item => this.permissionsService.hasAccess(item.route)))
  );

  bottomItems$ = combineLatest([
    this.authService.userRole,
    this.permissionsService.permissionsReady$
  ]).pipe(
    map(() => this.allBottomItems.filter(item => this.permissionsService.hasAccess(item.route)))
  );





  /**
   * Returns the contextual label for the New action button based on the current route.
   */
  getFabLabel(): string {
    const url = this.router.url;
    if (url.includes('fixed-expenses')) return 'Nuevo gasto fijo';
    if (url.includes('cards')) return 'Nuevo consumo';
    if (url.includes('groups')) return 'Nuevo grupo';
    if (url.includes('wallets')) return 'Nueva billetera';
    return 'Nueva transacción';
  }

  /**
   * Triggers the contextual action (e.g. open form) via the ActionService.
   */
  onNewActionClick(): void {
    this.actionService.triggerAction();
    if (this.screenService.isMobile()) {
      this.sidebarService.toggleSidebar();
    }
  }


  /**
   * Toggles the sidebar collapse state.
   */
  toggleSidebar() {
    this.sidebarService.toggleSidebar();
  }

  /**
   * Handles clicks on menu items.
   * Closes the sidebar if in mobile view.
   */
  onMenuItemClick() {
    if (this.screenService.isMobile()) {
      this.sidebarService.toggleSidebar();
    }
  }


  /**
   * Gets the CSS classes for the sidebar based on its state.
   * @returns A string of CSS classes.
   */
  getSidebarClasses(): string {
    const isCollapsed = this.sidebarService.getIsCollapsed();

    if (this.screenService.isMobile()) {

      return isCollapsed ? 'w-0 -translate-x-full opacity-0 pointer-events-none' : 'w-64 translate-x-0 opacity-100';
    } else {
      return isCollapsed ? 'w-[72px]' : 'w-48';
    }
  }

  /**
   * Checks if the sidebar is collapsed in desktop view.
   * @returns True if collapsed and not in mobile view.
   */
  isCollapsedDesktop(): boolean {
    return !this.screenService.isMobile() && this.sidebarService.getIsCollapsed();

  }

  /**
   * Checks if the sidebar is in mobile view.
   * @returns True if current view is mobile.
   */
  isMobile(): boolean {
    return this.screenService.isMobile();
  }

  /**
   * Checks if the app is in standalone mode (PWA).
   */
  isStandalone(): boolean {
    return this.screenService.isStandalone();
  }
}